/**
 * Job Sites API Client V2
 *
 * V2 endpoints for job site management with improved address handling
 */

import { apiClient } from '../client'

export interface JobSiteAddressV2 {
  street: string
  number: string
  postalCode: string
  city: string
  box?: string
  floor?: string
  country?: string
}

export interface JobSiteLookupMatchV2 {
  id: string
  reference: string
  address: JobSiteAddressV2
  formattedAddress?: string
}

export interface JobSiteLookupResponseV2 {
  matches: JobSiteLookupMatchV2[]
}

export interface JobSiteDetailsV2 {
  id: string
  reference?: string
  label?: string
  address: JobSiteAddressV2
}

export interface UpdateJobSitePayloadV2 {
  address: JobSiteAddressV2
  label?: string
}

export interface CreateJobSiteForProjectPayloadV2 {
  address: JobSiteAddressV2
}

export interface AssignExistingJobSitePayloadV2 {
  jobSiteId: string
}

export interface UpdateClientBillingAddressPayloadV2 {
  address: JobSiteAddressV2
}

/**
 * Lookup existing job sites by address (V2 wrapper around legacy)
 * POST /api/v2/job-sites/suggestions
 */
export async function lookupJobSitesByAddressV2(
  address: JobSiteAddressV2
): Promise<JobSiteLookupResponseV2> {
  const response = await apiClient.post<JobSiteLookupResponseV2>(
    '/api/v2/job-sites/suggestions',
    address
  )
  return response
}

/**
 * Get job site details by ID
 * GET /api/v2/job-sites/{jobSiteId}
 */
export async function getJobSiteDetailsV2(
  jobSiteId: string
): Promise<JobSiteDetailsV2> {
  const response = await apiClient.get<JobSiteDetailsV2>(
    `/api/v2/job-sites/${jobSiteId}`
  )
  return response
}

/**
 * Update job site address and optional label
 * PUT /api/v2/job-sites/{jobSiteId}
 */
export async function updateJobSiteV2(
  jobSiteId: string,
  payload: UpdateJobSitePayloadV2
): Promise<JobSiteDetailsV2> {
  const response = await apiClient.put<JobSiteDetailsV2>(
    `/api/v2/job-sites/${jobSiteId}`,
    payload
  )
  return response
}

/**
 * Create new job site and assign to project
 * POST /api/v2/projects/{projectId}/job-site
 */
export async function createJobSiteForProjectV2(
  projectId: string,
  payload: CreateJobSiteForProjectPayloadV2
): Promise<{ jobSiteId: string }> {
  const response = await apiClient.post<{ jobSiteId: string }>(
    `/api/v2/projects/${projectId}/job-site`,
    payload
  )
  return response
}

/**
 * Assign existing job site to project
 * PUT /api/v2/projects/{projectId}/job-site
 */
export async function assignJobSiteToProjectV2(
  projectId: string,
  payload: AssignExistingJobSitePayloadV2
): Promise<void> {
  await apiClient.put(
    `/api/v2/projects/${projectId}/job-site`,
    payload
  )
}

/**
 * Update client billing address
 * PUT /api/v2/clients/{customerId}/billing-address
 */
export async function updateClientBillingAddressV2(
  customerId: string,
  payload: UpdateClientBillingAddressPayloadV2
): Promise<void> {
  await apiClient.put(
    `/api/v2/clients/${customerId}/billing-address`,
    payload
  )
}
